// =========================================================================================@@
// Last Updated Date: May 24, 2025
// Last Updated By: Steven Yuen
// Status Level: 2
// ===========================================================================================

import React, { useContext, useEffect, useState } from 'react'
import { NotificationManagerContext, Text, View } from 'oio-react'
import { useOrganization, useUpdateInitiative } from 'src/core/graphql/hooks'
import { Link } from 'react-router-dom'
import MessageFeed from 'src/sites/kits/Messages/components/Feed'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { Title } from 'src/sites/kits/UI2'
import { cn } from 'src/sites/kits/Utils'

// We store the current user's preferred message display mode in local storage
const lsMessageDisplayModeKey = 'message-display-mode'

const ObjectCommentsBlock = () => {
   const { showNotification } = useContext(NotificationManagerContext)

   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()

   const isDraft = initiative.privacy === 'draft'
   const isPostOrDiscussion = ['discussion', 'post'].includes(initiative.class)
   const defaultMessageDisplayMode = isPostOrDiscussion ? 'conversation' : 'feed'
   const allowAnonSubmit = organization.slug === 'banff-pipeline-workshop'

   const [messageDisplayMode, setMessageDisplayMode] = useState(() => {
      const initialMessageDisplayMode =
         window.localStorage.getItem(lsMessageDisplayModeKey) || defaultMessageDisplayMode

      return initialMessageDisplayMode
   })

   useEffect(() => {
      window.localStorage.setItem(lsMessageDisplayModeKey, messageDisplayMode)
   }, [messageDisplayMode])

   const handleUpdateInitiative = async (value) => {
      try {
         await updateInitiative({ id: initiative.id }, value)

         showNotification({
            message: `${initiative.type.nameSingular} updated successfully`,
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   if (!initiative.appsMessagesEnabled) {
      return null
   }

   return (
      <>
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderTop="1px solid var(--primaryLineColor)"
            paddingTop="20px"
            marginBottom="20px">
            <Title size="sm">Conversation</Title>
            <div className="flex items-center h-8 rounded-lg bg-neutral-50 border border-solid border-neutral-200 overflow-hidden">
               <button
                  type="button"
                  className={cn(
                     'flex items-center px-3 h-full text-xs rounded-lg hover:opacity-70',
                     messageDisplayMode === 'conversation' ? 'bg-white shadow-lg ring-1 ring-neutral-200 opacity-100' : 'opacity-50'
                  )}
                  onClick={() => setMessageDisplayMode('conversation')}>
                  Oldest
               </button>
               <button
                  type="button"
                  onClick={() => setMessageDisplayMode('feed')}
                  className={cn(
                     'flex items-center px-3 h-full text-xs rounded-lg hover:opacity-70',
                     messageDisplayMode === 'feed' ? 'bg-white shadow-lg ring-1 ring-neutral-200 opacity-100' : 'opacity-50'
                  )}>
                  Newest
               </button>
            </div>
         </View>
         {isDraft && (
            <View backgroundColor="#f5f5f5" borderRadius="6px" padding="25px" textAlign="center">
               <Text size="1.5[a-b] 2[c-f]" color="#888">
                  {`Comments are not available while this ${initiative.type.nameSingular} is in draft mode.`}
               </Text>
            </View>
         )}
         {!isDraft && (
            <MessageFeed
               allowAnonSubmit={allowAnonSubmit}
               allowFileAttachments={!['event', 'session'].includes(initiative.class)}
               allowMarkClosingMessage={
                  !initiative.discussionStatus?.closed && initiative.discussionFormat === 'qa'
               }
               allowMessageCreate={!initiative.discussionStatus?.closed}
               closingMessageId={initiative.discussionStatus?.closingMessageId}
               currentUserCanCreate={initiative.appsMessagesCurrentUserCanCreate}
               currentUserCanModerate={initiative.currentUserCanModerateMessages}
               displayMode={messageDisplayMode}
               hasUnreadEntries={initiative.thread.hasUnreadEntries}
               messageLastViewed={initiative.thread.messageLastViewed}
               messageInputToolbarConfig={
                  ['event', 'session'].includes(initiative.class) ? 'textFull' : 'advanced'
               }
               onCloseMessageButtonClick={(closingMessageId) => {
                  handleUpdateInitiative({
                     discussionStatus: {
                        closed: true,
                        closingMessageId
                     }
                  })
               }}
            />
         )}
         {initiative.discussionStatus?.closed && (
            <View
               display="flex"
               justifyContent="center"
               float="left"
               width="100%"
               backgroundColor="#f5f5f5"
               borderRadius="6px"
               marginTop="40px"
               padding="25px">
               <Text size="1.5" color="#555">
                  <Link to={`/-/profile/${initiative.discussionStatus?.closedBy.id}`}>
                     <b style={{ color: '#222' }}>
                        {initiative.discussionStatus?.closedBy.fullName}
                     </b>
                  </Link>
                  {` closed this discussion on ${initiative.discussionStatus?.dateClosed}`}
               </Text>
            </View>
         )}
      </>
   )
}

export default ObjectCommentsBlock
